import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPricing = () => {
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setServices(response.data || []);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast({ title: "Error", description: "Failed to load pricing", variant: "destructive" });
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const startEdit = (service) => {
    setEditingService(service.id);
    setEditForm(JSON.parse(JSON.stringify(service))); // Deep copy
  };

  const cancelEdit = () => {
    setEditingService(null);
    setEditForm(null);
  };

  const saveService = async () => {
    try {
      const { id, ...serviceData } = editForm;
      await axios.put(`${API}/pricing/${id}`, serviceData, { headers: getAuthHeaders() });
      toast({ title: "Success", description: "Pricing updated successfully" });
      setEditingService(null);
      setEditForm(null);
      fetchPricing();
    } catch (error) {
      console.error('Error saving:', error);
      toast({ title: "Error", description: "Failed to save pricing", variant: "destructive" });
    }
  };

  const updateServiceName = (value) => {
    setEditForm({ ...editForm, serviceName: value });
  };

  const updateTier = (tierIndex, field, value) => {
    const newTiers = [...editForm.tiers];
    newTiers[tierIndex] = { ...newTiers[tierIndex], [field]: value };
    setEditForm({ ...editForm, tiers: newTiers });
  };

  const updateTierFeature = (tierIndex, featureIndex, value) => {
    const newTiers = [...editForm.tiers];
    const newFeatures = [...newTiers[tierIndex].features];
    newFeatures[featureIndex] = value;
    newTiers[tierIndex] = { ...newTiers[tierIndex], features: newFeatures };
    setEditForm({ ...editForm, tiers: newTiers });
  };

  const addFeature = (tierIndex) => {
    const newTiers = [...editForm.tiers];
    newTiers[tierIndex].features.push('New feature');
    setEditForm({ ...editForm, tiers: newTiers });
  };

  const removeFeature = (tierIndex, featureIndex) => {
    const newTiers = [...editForm.tiers];
    newTiers[tierIndex].features.splice(featureIndex, 1);
    setEditForm({ ...editForm, tiers: newTiers });
  };

  const toggleFeatured = (tierIndex) => {
    const newTiers = editForm.tiers.map((tier, idx) => ({
      ...tier,
      featured: idx === tierIndex ? !tier.featured : false
    }));
    setEditForm({ ...editForm, tiers: newTiers });
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-black">Manage Pricing</h2>
      </div>

      {services.map((service) => {
        const isEditing = editingService === service.id;
        const displayData = isEditing ? editForm : service;

        return (
          <div key={service.id} className="bg-white border-2 border-black/10 p-8">
            {/* Service Header */}
            <div className="flex justify-between items-start mb-6">
              {isEditing ? (
                <Input
                  value={displayData.serviceName}
                  onChange={(e) => updateServiceName(e.target.value)}
                  className="text-2xl font-bold h-auto border-black/20 max-w-md"
                />
              ) : (
                <h3 className="text-2xl font-bold text-black">{displayData.serviceName}</h3>
              )}
              
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button
                      onClick={saveService}
                      size="sm"
                      className="bg-black text-white hover:bg-black/90"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      onClick={cancelEdit}
                      size="sm"
                      variant="outline"
                      className="border-black/20"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => startEdit(service)}
                    size="sm"
                    variant="outline"
                    className="border-black/20"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </div>

            {/* Tiers Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayData.tiers.map((tier, tierIndex) => (
                <div
                  key={tier.id}
                  className={`border-2 p-6 ${
                    tier.featured ? 'border-black bg-black/5' : 'border-black/10'
                  }`}
                >
                  {isEditing ? (
                    <div className="space-y-4">
                      {/* Tier Name */}
                      <Input
                        value={tier.tierName}
                        onChange={(e) => updateTier(tierIndex, 'tierName', e.target.value)}
                        placeholder="Tier name"
                        className="font-bold border-black/20"
                      />

                      {/* Price */}
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold">₹</span>
                        <Input
                          value={tier.price}
                          onChange={(e) => updateTier(tierIndex, 'price', e.target.value)}
                          placeholder="Price"
                          className="text-2xl font-bold border-black/20"
                        />
                      </div>

                      {/* Featured Toggle */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tier.featured}
                          onChange={() => toggleFeatured(tierIndex)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Featured</span>
                      </label>

                      {/* Features */}
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-black/70 mb-2">Features:</div>
                        {tier.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex gap-2">
                            <Input
                              value={feature}
                              onChange={(e) =>
                                updateTierFeature(tierIndex, featureIndex, e.target.value)
                              }
                              className="text-sm border-black/20"
                            />
                            <Button
                              onClick={() => removeFeature(tierIndex, featureIndex)}
                              size="sm"
                              variant="outline"
                              className="border-black/20 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          onClick={() => addFeature(tierIndex)}
                          size="sm"
                          variant="outline"
                          className="w-full border-black/20"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Feature
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Display Mode */}
                      <div className="mb-4">
                        <h4 className="text-xl font-bold text-black mb-2">{tier.tierName}</h4>
                        <div className="text-3xl font-bold text-black">₹{tier.price}</div>
                        {tier.featured && (
                          <div className="mt-2 text-xs font-bold text-black/70">FEATURED</div>
                        )}
                      </div>
                      <ul className="space-y-2">
                        {tier.features.map((feature, idx) => (
                          <li key={idx} className="text-sm text-[#666666]">
                            • {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminPricing;
